import React, { useState } from 'react';
import { Dialog, Classes, Button, Intent } from '@blueprintjs/core';
import cc from 'classcat';
import Icon from '../../atoms/icon';
import Text from '../../atoms/text';
import Tooltip from '../tooltip';
import {
  faPlayCircle,
  faPauseCircle,
  faStopCircle
} from '@fortawesome/pro-solid-svg-icons';
import AnchorButton from '../../molecules/button/anchor';
import BPCheckbox from '../../atoms/checkbox/bpcheckbox';
import { DateRangeInput3 } from '@blueprintjs/datetime2';

import moment from 'moment';

import { useTriageUpdater } from '../../../contexts/triage/updater';
import { useMetaData } from '../../../contexts/metadata';
import { useApps } from '../../../contexts/apps';
import {
  IPatient,
  SavePatientResponse,
  GetFullName,
  SuspendPatientResponse
} from '../../../libs/patient';
import { PostJSON } from '../../../libs/request';
import { GetCurrentTZ } from '../../../libs/time';
import { getSuspensionDates, ITask } from '../../../libs/tasks';
import { TriagedApp } from '../../../contexts/triage/types';

type PauseActionModes = 'pause' | 'resume' | 'cancelSuspension';
type StepStages = 'dateSelection' | 'programSelection' | 'finalConfirm';

// Utilities

const getActionText = (mode: PauseActionModes, step: StepStages) => {
  if (mode === 'resume' || mode === 'cancelSuspension') {
    return 'OK';
  }
  if (step === 'finalConfirm') return 'OK';
  return 'Next';
};

const getStartDate = (tasks: ITask[]) => {
  if (tasks) {
    const suspensionDates = getSuspensionDates(tasks);
    if (suspensionDates) {
      return suspensionDates.startDate || null;
    }
  }
  return null;
};

const getMode = (patient: IPatient, startDate: string) => {
  if (patient.isSuspended) {
    return 'resume';
  }
  const upcomingTask = startDate && moment(startDate).isAfter(moment());
  if (upcomingTask) {
    return 'cancelSuspension';
  }
  return 'pause';
};

const getIconAndTitle = (mode: PauseActionModes) => {
  switch (mode) {
    case 'pause':
      return {
        icon: faPauseCircle,
        title: 'Pause Service'
      };
    case 'resume':
      return {
        icon: faPlayCircle,
        title: 'Resume Service'
      };
    case 'cancelSuspension':
      return {
        icon: faStopCircle,
        title: 'Cancel Suspension'
      };
  }
};

const isDateValid = (mode: PauseActionModes, dateRange: [Date, Date]) => {
  if (mode === 'pause') {
    return dateRange[0] !== null && dateRange[1] !== null;
  }
  return true;
};

// Child components

interface DateSelectionStepProps {
  patient: IPatient;
  tz: string;
  dateRange: [Date, Date];
  todayDate: Date;
  setDateRange: (range: [Date, Date]) => void;
}

const DateSelectionStep = ({
  patient,
  tz,
  dateRange,
  todayDate,
  setDateRange
}: DateSelectionStepProps) => {
  return (
    <div className="py-4 px-0">
      <p className="bp5-running-text">
        Please choose a start and end date for{' '}
        <strong>{GetFullName(patient)}</strong> to be paused.
      </p>
      <DateRangeInput3
        formatDate={(date) => moment.tz(date, tz).format('YYYY-MM-DD')}
        parseDate={(str) => {
          const m = moment.tz(str, tz);
          return m.isValid() ? m.toDate() : new Date(NaN);
        }}
        value={dateRange}
        minDate={todayDate}
        closeOnSelection={true}
        popoverProps={{
          usePortal: true,
          placement: 'auto',
          modifiers: {
            preventOverflow: { enabled: true },
            flip: { enabled: true }
          }
        }}
        onChange={(range) => setDateRange(range)}
      />
    </div>
  );
};

interface ProgramSelectionStepProps {
  patient: IPatient;
  apps?: { [key: string]: TriagedApp };
  entirePatient: boolean;
  setEntirePatient: (v: boolean) => void;
  selectedApps: string[];
  setSelectedApps: (apps: string[]) => void;
}

const ProgramSelectionStep = ({
  patient,
  apps,
  entirePatient,
  setEntirePatient,
  selectedApps,
  setSelectedApps
}: ProgramSelectionStepProps) => {
  const { data: appsData } = useApps();
  const appConfigs = appsData?.apps || {};

  const appKeys = apps ? Object.keys(apps).filter((k) => k !== 'composer') : [];

  const handleEntirePatientToggle = () => {
    if (!entirePatient) {
      setEntirePatient(true);
      setSelectedApps([]);
    } else {
      setEntirePatient(false);
    }
  };

  const handleAppToggle = (appKey: string) => {
    if (entirePatient) return;
    if (selectedApps.includes(appKey)) {
      const next = selectedApps.filter((a) => a !== appKey);
      setSelectedApps(next);
    } else {
      const next = [...selectedApps, appKey];
      if (next.length === appKeys.length) {
        // All programs selected — switch to entire patient
        setEntirePatient(true);
        setSelectedApps([]);
      } else {
        setSelectedApps(next);
      }
    }
  };

  return (
    <div className="py-4 px-0">
      <p className="bp5-running-text">
        Choose which programs to pause for{' '}
        <strong>{GetFullName(patient)}</strong>, or pause the entire patient.
      </p>
      <div className="mt-2">
        <BPCheckbox
          checked={entirePatient}
          onChange={handleEntirePatientToggle}
          label="Entire Patient (all programs)"
          className={entirePatient ? 'font-bold' : ''}
        />
        <div className="ml-6 mt-1 border-l-solid border-2 border-light-grey pl-3">
          {appKeys.map((key) => {
            const config = appConfigs[key];
            const appEntry = apps[key];
            let name = config?.name || key;
            if (appEntry && 'name' in appEntry) {
              name = (appEntry as any).name || name;
            }
            if (appEntry?.customProgram?.name) {
              name = appEntry.customProgram.name;
            }
            return (
              <BPCheckbox
                key={key}
                checked={selectedApps.includes(key)}
                onChange={() => handleAppToggle(key)}
                label={name}
                disabled={entirePatient}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface FinalConfirmStepProps {
  patient: IPatient;
  tz: string;
  dateRange: [Date, Date];
  selectedApps: string[];
  apps?: { [key: string]: TriagedApp };
}

const FinalConfirmStep = ({
  patient,
  tz,
  dateRange,
  selectedApps,
  apps
}: FinalConfirmStepProps) => {
  const { terms } = useMetaData();
  const { data: appsData } = useApps();
  const appConfigs = appsData?.apps || {};
  const [start, end] = dateRange;
  const startStr = start ? moment.tz(start, tz).format('YYYY-MM-DD') : '';
  const endStr = end ? moment.tz(end, tz).format('YYYY-MM-DD') : 'N/A';

  const getProgramNames = () => {
    if (selectedApps.length === 0) return 'all programs';
    return selectedApps
      .map((key) => {
        const config = appConfigs[key];
        const appEntry = apps?.[key];
        let name = config?.name || key;
        if (appEntry?.customProgram?.name) {
          name = appEntry.customProgram.name;
        }
        return name;
      })
      .join(', ');
  };

  return (
    <div className="py-4 px-0">
      <p className="bp5-running-text mb-4">
        <strong>{GetFullName(patient)}</strong> will be paused from the start of{' '}
        <strong>{startStr}</strong> to the start of <strong>{endStr}</strong>.
        {selectedApps.length > 0 ? (
          <>
            {' '}
            The following programs will be paused:{' '}
            <strong>{getProgramNames()}</strong>. Other active programs will
            continue sending communications.
          </>
        ) : (
          <> No communications will be sent during this period.</>
        )}
      </p>
      <ul className="bp5-list-unstyled ml-4 mb-4">
        <li>
          Only pause service if you plan to resume it again later (e.g., when
          the {terms.patient.default} is hospitalized or on vacation).
        </li>
        <li>
          Paused {terms.patient.plural} remain on your dashboard but won't
          receive communications
          {selectedApps.length > 0 ? ' for the selected programs' : ''}.
        </li>
        <li>
          If you want to remove this {terms.patient.default} from your dashboard
          completely, use <strong>Archive {terms.patient.capitalized}</strong>{' '}
          instead.
        </li>
      </ul>
      <p className="bp5-running-text">
        <strong>Are you sure you want to proceed?</strong>
      </p>
    </div>
  );
};

interface PatientProps {
  patient: IPatient;
}

const ResumeContent = ({ patient }: PatientProps) => (
  <div className="py-4 px-0">
    <p className="bp5-running-text">
      <b>{GetFullName(patient)}</b> will start receiving communications again
      from any active programs. Are you sure you want to continue?
    </p>
  </div>
);

const CancelSuspensionContent = ({ patient }: PatientProps) => (
  <div className="py-4 px-0">
    <p className="bp5-running-text">
      There is an upcoming suspension scheduled. Are you sure you want to cancel
      the upcoming suspension for <strong>{GetFullName(patient)}</strong>?
    </p>
  </div>
);

/**
 * This action kicks off the
 * patient pause flow
 */

interface PausePatientActionProps {
  patient: IPatient;
  apps?: { [key: string]: TriagedApp };
  bare?: boolean;
  className?: string;
  fill?: boolean;
  compact?: boolean;
}

export const PausePatientAction = ({
  patient,
  apps,
  className,
  bare = false,
  fill = false,
  compact = false
}: PausePatientActionProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<StepStages>('dateSelection');
  const [dateRange, setDateRange] = useState<[Date, Date]>([null, null]);
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [entirePatient, setEntirePatient] = useState(false);
  const { handleTogglePausePatient } = useTriageUpdater();
  const tz = GetCurrentTZ();
  const startDate = getStartDate(patient.tasks);
  const mode = getMode(patient, startDate);
  const { icon, title } = getIconAndTitle(mode);
  const classNames = [className, 'justify-left'];

  const todayMoment = moment.tz(tz).startOf('day');
  const todayDate = todayMoment.toDate();

  // Determine if there are individual programs available for program-specific pause
  const appKeys = apps ? Object.keys(apps).filter((k) => k !== 'composer') : [];
  const hasIndividualPrograms = appKeys.length > 0;

  const handleFinalConfirm = async () => {
    const [pickedStart, pickedEnd] = dateRange;
    let finalStart = null;
    let finalEnd = null;
    let cancelSuspension = false;

    if (!patient.isSuspended) {
      // Suspension flow
      finalStart = pickedStart;
      finalEnd = pickedEnd;
    }

    if (mode === 'cancelSuspension') {
      cancelSuspension = true;
    }

    const response = await PostJSON<SavePatientResponse>({
      url: '/patient/suspend/',
      params: {
        id: patient.id,
        // Always toggle isSuspended
        isSuspended: !patient.isSuspended,
        // Flag to cancel upcoming suspension
        cancelSuspension: cancelSuspension,
        // Current timezone
        tz: tz,
        // Conditionally add start/end if we're pausing
        ...(finalStart && {
          startDate: moment.tz(finalStart, tz).format('YYYY-MM-DD')
        }),
        ...(finalEnd && {
          endDate: moment.tz(finalEnd, tz).format('YYYY-MM-DD')
        }),
        // Program-specific pause: send apps array if individual programs selected
        ...(!entirePatient && selectedApps.length > 0 && { apps: selectedApps })
      }
    });

    if (response.status !== 'OK') {
      alert(response.message);
      return;
    }
    handleTogglePausePatient(
      (response as SuspendPatientResponse).patient,
      cancelSuspension,
      (response as SuspendPatientResponse).tasks ?? patient.tasks ?? [],
      (response as SuspendPatientResponse).pausedApps
    );
    setIsOpen(false);
  };

  // Get Modal content
  const getDialogBody = () => {
    if (mode === 'cancelSuspension') {
      return <CancelSuspensionContent patient={patient} />;
    }
    if (patient.isSuspended) {
      return <ResumeContent patient={patient} />;
    }
    if (step === 'dateSelection') {
      return (
        <DateSelectionStep
          patient={patient}
          tz={tz}
          dateRange={dateRange}
          todayDate={todayDate}
          setDateRange={setDateRange}
        />
      );
    }
    if (step === 'programSelection') {
      return (
        <ProgramSelectionStep
          patient={patient}
          apps={apps}
          entirePatient={entirePatient}
          setEntirePatient={setEntirePatient}
          selectedApps={selectedApps}
          setSelectedApps={setSelectedApps}
        />
      );
    }
    return (
      <FinalConfirmStep
        patient={patient}
        tz={tz}
        dateRange={dateRange}
        selectedApps={selectedApps}
        apps={apps}
      />
    );
  };

  const handlePrimaryAction = () => {
    if (mode === 'resume' || mode === 'cancelSuspension') {
      handleFinalConfirm();
    } else {
      if (step === 'dateSelection') {
        if (hasIndividualPrograms) {
          setStep('programSelection');
        } else {
          // No individual programs — skip program selection, default to full pause
          setEntirePatient(true);
          setStep('finalConfirm');
        }
      } else if (step === 'programSelection') {
        setStep('finalConfirm');
      } else {
        handleFinalConfirm();
      }
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleOpen = () => {
    if (mode === 'pause') {
      setStep('dateSelection');
      setDateRange([null, null]);
      setSelectedApps([]);
      setEntirePatient(false);
    }
    setIsOpen(true);
  };

  return (
    <>
      <AnchorButton
        className={cc(classNames)}
        onClick={handleOpen}
        bare={bare}
        fill={fill}
        variant="minimal"
      >
        {compact ? (
          <Tooltip content={title}>
            <Icon icon={icon} title={title} />
          </Tooltip>
        ) : (
          <>
            <Icon className="mr-2" icon={icon} title={title} />
            <Text dotted>
              <span role="status">{title}</span>
            </Text>
          </>
        )}
      </AnchorButton>

      <Dialog
        isOpen={isOpen}
        onClose={handleCancel}
        title={title}
        style={{ width: 600 }}
      >
        <div className={Classes.DIALOG_BODY}>{getDialogBody()}</div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              intent={Intent.PRIMARY}
              onClick={handlePrimaryAction}
              disabled={
                !isDateValid(mode, dateRange) ||
                (step === 'programSelection' &&
                  !entirePatient &&
                  selectedApps.length === 0)
              }
            >
              {getActionText(mode, step)}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
